import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiMousePointer, FiMail, FiFileText, FiGlobe, FiMonitor, FiSmartphone, FiTablet, FiClock, FiRefreshCw, FiCalendar, FiArrowRight, FiDownload, FiActivity, FiTarget, FiZap, FiUsers, FiMapPin, FiLayers, FiList } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend } from 'recharts';
import { getAnalytics, exportAnalyticsCSV, exportAnalyticsPDF, getHeatmapData, getRealtimeVisitors } from '../../services/api';
import SessionHistory from './SessionHistory';

const COLORS = ['#101957', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

// Time Period Card Component (moved outside to prevent re-renders)
const TimePeriodCard = ({ title, icon: Icon, data, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white p-5 rounded-lg shadow-sm border-l-4"
    style={{ borderLeftColor: color }}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center">
        <Icon className="text-lg mr-2" style={{ color }} />
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <div className="text-gray-500">Visits</div>
        <div className="text-xl font-bold text-dark-900">{data?.visits || 0}</div>
      </div>
      <div>
        <div className="text-gray-500">Inquiries</div>
        <div className="text-xl font-bold text-green-600">{data?.inquiries || 0}</div>
      </div>
      <div>
        <div className="text-gray-500">CTA Clicks</div>
        <div className="text-lg font-semibold text-blue-600">{data?.cta_clicks || 0}</div>
      </div>
      <div>
        <div className="text-gray-500">RFQ Uploads</div>
        <div className="text-lg font-semibold text-orange-600">{data?.rfq_uploads || 0}</div>
      </div>
    </div>
  </motion.div>
);

// Conversion Funnel Component (moved outside to prevent re-renders)
const ConversionFunnel = ({ data }) => {
  if (!data) return null;
  
  const steps = [
    { label: 'Visits', value: data.visits, color: '#101957' },
    { label: 'CTA Clicks', value: data.cta_clicks, rate: data.visit_to_cta_rate, color: '#0ea5e9' },
    { label: 'Inquiries', value: data.inquiries, rate: data.cta_to_inquiry_rate, color: '#10b981' },
    { label: 'RFQ Uploads', value: data.rfq_uploads, rate: data.inquiry_to_rfq_rate, color: '#f59e0b' }
  ];
  
  const maxValue = Math.max(...steps.map(s => s.value)) || 1;
  
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div key={step.label} className="flex items-center">
          <div className="w-28 text-sm font-medium text-gray-700">{step.label}</div>
          <div className="flex-1 mx-4">
            <div className="relative">
              <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(step.value / maxValue) * 100}%` }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="h-full rounded-full flex items-center justify-end pr-3"
                  style={{ backgroundColor: step.color }}
                >
                  <span className="text-white text-sm font-bold">{step.value}</span>
                </motion.div>
              </div>
            </div>
          </div>
          {step.rate !== undefined && (
            <div className="w-20 text-right">
              <span className="text-sm font-semibold" style={{ color: step.color }}>
                {step.rate}%
              </span>
              <div className="text-xs text-gray-500">conv.</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [realtimeVisitors, setRealtimeVisitors] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [trendView, setTrendView] = useState('daily'); // daily, weekly, monthly, yearly
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, heatmap, realtime

  useEffect(() => {
    loadAnalytics();
    loadHeatmapData();
  }, [days]);

  const loadRealtimeVisitors = useCallback(async () => {
    try {
      const response = await getRealtimeVisitors();
      setRealtimeVisitors(response.data);
    } catch (error) {
      console.error('Failed to load realtime visitors:', error);
    }
  }, []);

  // Poll real-time visitors every 10 seconds - always active regardless of tab
  useEffect(() => {
    // Load immediately on mount
    loadRealtimeVisitors();
    // Then poll every 10 seconds
    const interval = setInterval(loadRealtimeVisitors, 10000);
    return () => clearInterval(interval);
  }, [loadRealtimeVisitors]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const response = await getAnalytics(days);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHeatmapData = async () => {
    try {
      const response = await getHeatmapData(days);
      setHeatmapData(response.data);
    } catch (error) {
      console.error('Failed to load heatmap data:', error);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await exportAnalyticsCSV(days);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `haden_analytics_${days}days.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const response = await exportAnalyticsPDF(days);
      // Open HTML in new window for print-to-PDF
      const newWindow = window.open('', '_blank');
      newWindow.document.write(response.data);
      newWindow.document.close();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  // Get trend data based on selected view
  const getTrendData = () => {
    if (!analytics?.trend_data) return [];
    
    switch (trendView) {
      case 'daily':
        return analytics.trend_data.hourly?.map(item => ({
          ...item,
          label: item.time
        })) || [];
      case 'weekly':
        return analytics.trend_data.daily?.slice(-7).map(item => ({
          ...item,
          label: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' })
        })) || [];
      case 'monthly':
        return analytics.trend_data.daily?.map(item => ({
          ...item,
          label: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        })) || [];
      case 'yearly':
        return analytics.trend_data.monthly?.map(item => ({
          ...item,
          label: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short' })
        })) || [];
      default:
        return analytics.daily_stats || [];
    }
  };

  const trendData = getTrendData();

  // Prepare device data for pie chart
  const deviceData = analytics?.device_breakdown 
    ? Object.entries(analytics.device_breakdown)
        .filter(([_, count]) => count > 0)
        .map(([name, value]) => ({ name, value }))
    : [];

  // Prepare browser data
  const browserData = analytics?.browser_breakdown 
    ? Object.entries(analytics.browser_breakdown)
        .slice(0, 5)
        .map(([name, value]) => ({ name, value }))
    : [];

  // Prepare inquiry type data
  const inquiryTypeData = analytics?.inquiry_types 
    ? Object.entries(analytics.inquiry_types)
        .map(([name, value]) => ({ name, value }))
    : [];

  // Prepare day of week data
  const dayOfWeekData = analytics?.day_of_week 
    ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        .map(day => ({ day: day.slice(0, 3), visits: analytics.day_of_week[day] || 0 }))
    : [];

  return (
    <div data-testid="analytics-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-dark-900">Analytics Dashboard</h1>
        <div className="flex items-center gap-4">
          {/* Export Buttons */}
          <div className="flex items-center gap-2 border-r border-gray-200 pr-4">
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <FiDownload className="mr-1.5" size={14} />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <FiDownload className="mr-1.5" size={14} />
              PDF
            </button>
          </div>
          <button
            onClick={() => { loadAnalytics(); loadHeatmapData(); if (activeTab === 'realtime') loadRealtimeVisitors(); }}
            className="flex items-center px-3 py-2 text-gray-600 hover:text-primary-600"
          >
            <FiRefreshCw className="mr-2" />
            Refresh
          </button>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'overview'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiTrendingUp className="inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'sessions'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiList className="inline mr-2" />
            Session History
          </button>
          <button
            onClick={() => setActiveTab('heatmap')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'heatmap'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiLayers className="inline mr-2" />
            Heatmap
          </button>
          <button
            onClick={() => setActiveTab('realtime')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px flex items-center ${
              activeTab === 'realtime'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <FiUsers className="inline mr-2" />
            Real-time
            <span className={`ml-2 px-2 py-0.5 text-white text-xs rounded-full ${
              realtimeVisitors?.total_active > 0 
                ? 'bg-green-500 animate-pulse' 
                : 'bg-gray-400'
            }`}>
              {realtimeVisitors?.total_active ?? 0}
            </span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {/* Session History Tab */}
      {activeTab === 'sessions' && (
        <SessionHistory />
      )}

      {activeTab === 'overview' && (
        <>
      {/* Time Period Summary Cards */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
          <FiCalendar className="mr-2" />
          Performance Overview
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <TimePeriodCard 
            title="Today" 
            icon={FiClock} 
            data={analytics?.time_summaries?.today}
            color="#101957"
          />
          <TimePeriodCard 
            title="This Week" 
            icon={FiCalendar} 
            data={analytics?.time_summaries?.this_week}
            color="#0ea5e9"
          />
          <TimePeriodCard 
            title="This Month" 
            icon={FiCalendar} 
            data={analytics?.time_summaries?.this_month}
            color="#10b981"
          />
          <TimePeriodCard 
            title="This Year" 
            icon={FiCalendar} 
            data={analytics?.time_summaries?.this_year}
            color="#f59e0b"
          />
        </div>
      </div>

      {/* Period Summary Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary-600 to-primary-800 p-6 rounded-lg shadow-sm text-white"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-white/80 text-sm font-medium">Total Visits ({days}d)</div>
            <FiTrendingUp className="text-white/80 text-xl" />
          </div>
          <div className="text-3xl font-bold">
            {analytics?.summary?.total_visits || 0}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-gray-600 text-sm font-medium">CTA Clicks</div>
            <FiMousePointer className="text-blue-600 text-xl" />
          </div>
          <div className="text-3xl font-bold text-dark-900">
            {analytics?.summary?.total_cta_clicks || 0}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-gray-600 text-sm font-medium">Inquiries</div>
            <FiMail className="text-green-600 text-xl" />
          </div>
          <div className="text-3xl font-bold text-dark-900">
            {analytics?.summary?.total_inquiries || 0}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-lg shadow-sm"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-gray-600 text-sm font-medium">RFQ Uploads</div>
            <FiFileText className="text-orange-600 text-xl" />
          </div>
          <div className="text-3xl font-bold text-dark-900">
            {analytics?.summary?.total_rfq_uploads || 0}
          </div>
        </motion.div>
      </div>

      {/* Advanced Analytics: Engagement Metrics */}
      {analytics?.engagement_metrics && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-xl font-bold text-dark-900 mb-6 flex items-center">
            <FiActivity className="mr-2 text-purple-600" />
            Engagement Metrics
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-purple-50 rounded-lg group relative cursor-help">
              <div className="text-3xl font-bold text-purple-700">
                {analytics.engagement_metrics.total_sessions || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Total Sessions</div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-52 text-center z-10">
                Total number of visits in the selected time period. Not real-time — this shows historical visit count.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg group relative cursor-help">
              <div className="text-3xl font-bold text-blue-700">
                {analytics.engagement_metrics.avg_session_duration_formatted || '0:00'}
              </div>
              <div className="text-sm text-gray-600 mt-1">Avg. Session Duration</div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 text-center z-10">
                Average time visitors spend on your site per session. Longer duration indicates higher engagement.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg group relative cursor-help">
              <div className="text-3xl font-bold text-green-700">
                {analytics.engagement_metrics.pages_per_session || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Pages per Session</div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 text-center z-10">
                Average number of pages viewed per session. Higher values mean visitors explore more content.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg group relative cursor-help">
              <div className="text-3xl font-bold text-red-600">
                {analytics.engagement_metrics.bounce_rate || 0}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Bounce Rate</div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 text-center z-10">
                26% bounce rate means 74% of visitors explored your site. The remaining 26% left after seeing just one page. Lower = more engaged visitors.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Analytics: Top Performing Days & Hours */}
      {analytics?.top_performing && (
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiTarget className="mr-2 text-green-600" />
              Top Performing Days
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-green-50 rounded-lg text-center group relative cursor-help">
                <div className="text-lg font-bold text-green-700">
                  {analytics.top_performing.best_day_for_visits?.day || 'N/A'}
                </div>
                <div className="text-xs text-gray-500">Best Day for Visits</div>
                <div className="text-sm text-gray-600 mt-1">
                  {analytics.top_performing.best_day_for_visits?.count || 0} visits
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 text-center z-10">
                  The day of the week when your website receives the most traffic. Focus marketing efforts on this day.
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center group relative cursor-help">
                <div className="text-lg font-bold text-amber-700">
                  {analytics.top_performing.best_day_for_conversions?.day || 'N/A'}
                </div>
                <div className="text-xs text-gray-500">Best Day for Conversions</div>
                <div className="text-sm text-gray-600 mt-1">
                  {analytics.top_performing.best_day_for_conversions?.count || 0} inquiries
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 text-center z-10">
                  The day when visitors most often submit inquiries. Best day to run promotions or send newsletters.
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            {dayOfWeekData.length > 0 && (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={dayOfWeekData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="visits" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiClock className="mr-2 text-indigo-600" />
              Peak Activity Hours
            </h2>
            <div className="p-4 bg-indigo-50 rounded-lg text-center mb-4 group relative cursor-help">
              <div className="text-2xl font-bold text-indigo-700">
                {analytics.top_performing.best_hour_for_activity?.hour || '00:00'}
              </div>
              <div className="text-xs text-gray-500">Peak Hour</div>
              <div className="text-sm text-gray-600 mt-1">
                {analytics.top_performing.best_hour_for_activity?.count || 0} events
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-52 text-center z-10">
                The hour of day (IST) when your site has the most activity. Schedule important updates or posts during this time.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
            {analytics.top_performing.hour_breakdown && (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={Object.entries(analytics.top_performing.hour_breakdown).map(([hour, count]) => ({ hour: `${hour} IST`, count }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={3} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#c7d2fe" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Advanced Analytics: User Journey Flow */}
      {analytics?.user_journey && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-xl font-bold text-dark-900 mb-6 flex items-center">
            <FiZap className="mr-2 text-amber-500" />
            User Journey Flow
          </h2>
          <div className="flex items-center justify-center flex-wrap gap-4">
            <div className="text-center p-6 bg-blue-100 rounded-xl min-w-[140px]">
              <div className="text-3xl font-bold text-blue-700">
                {analytics.user_journey.stages?.stage_1_visits || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Visitors</div>
            </div>
            <div className="flex flex-col items-center text-gray-400">
              <FiArrowRight size={24} />
              <span className="text-xs mt-1">{analytics.user_journey.conversion_rates?.visit_to_cta || 0}%</span>
            </div>
            <div className="text-center p-6 bg-amber-100 rounded-xl min-w-[140px]">
              <div className="text-3xl font-bold text-amber-700">
                {analytics.user_journey.stages?.stage_2_cta_clicks || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">CTA Clicks</div>
            </div>
            <div className="flex flex-col items-center text-gray-400">
              <FiArrowRight size={24} />
              <span className="text-xs mt-1">{analytics.user_journey.conversion_rates?.cta_to_inquiry || 0}%</span>
            </div>
            <div className="text-center p-6 bg-green-100 rounded-xl min-w-[140px]">
              <div className="text-3xl font-bold text-green-700">
                {analytics.user_journey.stages?.stage_3_inquiries || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Inquiries</div>
            </div>
            <div className="flex flex-col items-center text-gray-400">
              <FiArrowRight size={24} />
              <span className="text-xs mt-1">{analytics.user_journey.conversion_rates?.inquiry_to_rfq || 0}%</span>
            </div>
            <div className="text-center p-6 bg-purple-100 rounded-xl min-w-[140px]">
              <div className="text-3xl font-bold text-purple-700">
                {analytics.user_journey.stages?.stage_4_rfq_uploads || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">RFQ Uploads</div>
            </div>
          </div>
          <div className="text-center mt-6 p-4 bg-gray-50 rounded-lg">
            <span className="text-gray-600">Overall Conversion Rate: </span>
            <span className="text-xl font-bold text-green-600">
              {analytics.user_journey.conversion_rates?.overall || 0}%
            </span>
            <span className="text-gray-500 text-sm ml-2">(Visitors → Inquiries)</span>
          </div>
        </div>
      )}

      {/* Activity Trend Chart with Toggle */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-dark-900">Activity Trend</h2>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { key: 'daily', label: 'Daily' },
              { key: 'weekly', label: 'Weekly' },
              { key: 'monthly', label: 'Monthly' },
              { key: 'yearly', label: 'Yearly' }
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setTrendView(option.key)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  trendView === option.key
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#101957" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#101957" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorInquiries" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="visits" stroke="#101957" fillOpacity={1} fill="url(#colorVisits)" name="Visits" />
            <Area type="monotone" dataKey="inquiries" stroke="#10b981" fillOpacity={1} fill="url(#colorInquiries)" name="Inquiries" />
            <Line type="monotone" dataKey="cta_clicks" stroke="#0ea5e9" strokeWidth={2} dot={false} name="CTA Clicks" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-4 text-xs text-gray-500 text-center">
          {trendView === 'daily' && 'Showing hourly breakdown for today (IST)'}
          {trendView === 'weekly' && 'Showing daily breakdown for the past 7 days'}
          {trendView === 'monthly' && `Showing daily breakdown for the past ${days} days`}
          {trendView === 'yearly' && 'Showing monthly breakdown for this year'}
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-dark-900 mb-6 flex items-center">
          <FiArrowRight className="mr-2 text-primary-600" />
          Conversion Funnel
        </h2>
        <ConversionFunnel data={analytics?.conversion_funnel} />
      </div>

      {/* Two column layout for charts */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Geographic Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
            <FiGlobe className="mr-2 text-primary-600" />
            Geographic Distribution
          </h2>
          {analytics?.geo_breakdown && analytics.geo_breakdown.length > 0 ? (
            <div className="space-y-3">
              {analytics.geo_breakdown.slice(0, 8).map((geo, index) => (
                <div key={geo.country} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <div>
                      <span className="font-medium text-dark-900">{geo.country}</span>
                      {geo.top_cities && geo.top_cities.length > 0 && (
                        <span className="text-xs text-gray-500 ml-2">
                          ({geo.top_cities.map(c => c.city).join(', ')})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="h-2 rounded-full"
                        style={{ 
                          width: `${Math.min(100, (geo.count / (analytics.summary?.total_visits || 1)) * 100)}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-12 text-right">{geo.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No geographic data available yet
            </div>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
            <FiMonitor className="mr-2 text-primary-600" />
            Device Distribution
          </h2>
          {deviceData.length > 0 ? (
            <div className="flex items-center">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {deviceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {deviceData.map((device, index) => (
                  <div key={device.name} className="flex items-center">
                    {device.name === 'Desktop' && <FiMonitor className="mr-2 text-lg" style={{ color: COLORS[index] }} />}
                    {device.name === 'Mobile' && <FiSmartphone className="mr-2 text-lg" style={{ color: COLORS[index] }} />}
                    {device.name === 'Tablet' && <FiTablet className="mr-2 text-lg" style={{ color: COLORS[index] }} />}
                    {!['Desktop', 'Mobile', 'Tablet'].includes(device.name) && (
                      <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[index] }}></div>
                    )}
                    <span className="text-sm text-gray-700">{device.name}: </span>
                    <span className="text-sm font-semibold ml-1">{device.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No device data available yet
            </div>
          )}
        </div>
      </div>

      {/* Additional Charts Row */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Day of Week Performance */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Traffic by Day of Week</h2>
          {dayOfWeekData.some(d => d.visits > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dayOfWeekData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="visits" fill="#101957" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No data available yet
            </div>
          )}
        </div>

        {/* Inquiry Types Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Inquiry Types</h2>
          {inquiryTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={inquiryTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No inquiry data available yet
            </div>
          )}
        </div>
      </div>

      {/* Browser and Traffic Sources */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Browser Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Browser Distribution</h2>
          {browserData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={browserData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No browser data available yet
            </div>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Traffic Sources</h2>
          {analytics?.traffic_sources && Object.keys(analytics.traffic_sources).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.traffic_sources).slice(0, 6).map(([source, count], index) => (
                <div key={source} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className="text-sm text-gray-700 truncate max-w-48">{source || 'Direct'}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No traffic source data available yet
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Heatmap Tab */}
      {activeTab === 'heatmap' && (
        <div className="space-y-8">
          {/* Heatmap Summary */}
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-lg shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-gray-600 text-sm font-medium">Total Click Events</div>
                <FiMousePointer className="text-blue-600 text-xl" />
              </div>
              <div className="text-3xl font-bold text-dark-900">
                {heatmapData?.summary?.total_clicks || 0}
              </div>
              <p className="text-xs text-gray-500 mt-2">Last {days} days</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-gray-600 text-sm font-medium">Tracked Sections</div>
                <FiLayers className="text-green-600 text-xl" />
              </div>
              <div className="text-3xl font-bold text-dark-900">
                {heatmapData?.click_heatmap ? Object.keys(heatmapData.click_heatmap).length : 0}
              </div>
              <p className="text-xs text-gray-500 mt-2">Active sections</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-gray-600 text-sm font-medium">Total Events</div>
                <FiActivity className="text-purple-600 text-xl" />
              </div>
              <div className="text-3xl font-bold text-dark-900">
                {heatmapData?.summary?.total_events || 0}
              </div>
              <p className="text-xs text-gray-500 mt-2">Clicks + Scrolls + Time</p>
            </motion.div>
          </div>

          {/* Click Heatmap by Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiMousePointer className="mr-2 text-blue-600" />
              Clicks by Section
            </h2>
            {heatmapData?.click_heatmap && Object.keys(heatmapData.click_heatmap).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(heatmapData.click_heatmap)
                  .sort((a, b) => b[1].total_clicks - a[1].total_clicks)
                  .map(([section, data], index) => (
                  <div key={section} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-medium text-dark-900">{data.label || section}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="h-2 rounded-full bg-blue-500"
                          style={{ 
                            width: `${Math.min(100, (data.total_clicks / (heatmapData.summary?.total_clicks || 1)) * 100)}%`
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 w-12 text-right">{data.total_clicks}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FiLayers className="mx-auto text-4xl mb-3 text-gray-300" />
                <p>No heatmap data available yet</p>
                <p className="text-sm mt-1">Heatmap tracking is active. Data will appear as users interact with your site.</p>
              </div>
            )}
          </div>

          {/* Time on Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiClock className="mr-2 text-green-600" />
              Average Time on Section
            </h2>
            {heatmapData?.time_on_section && Object.keys(heatmapData.time_on_section).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={Object.entries(heatmapData.time_on_section)
                    .map(([section, data]) => ({
                      section: data.label || section,
                      avgTime: data.avg_time_seconds || 0,
                      visits: data.visits || 0
                    }))
                    .sort((a, b) => b.avgTime - a.avgTime)}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" label={{ value: 'Seconds', position: 'bottom' }} />
                  <YAxis type="category" dataKey="section" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value}s`, 'Avg Time']} />
                  <Bar dataKey="avgTime" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FiClock className="mx-auto text-4xl mb-3 text-gray-300" />
                <p>No time tracking data available yet</p>
              </div>
            )}
          </div>

          {/* Scroll Depth */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiArrowRight className="mr-2 text-purple-600 rotate-90" />
              Scroll Depth by Section
            </h2>
            {heatmapData?.scroll_depth && Object.keys(heatmapData.scroll_depth).length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(heatmapData.scroll_depth).map(([section, data], index) => (
                  <div key={section} className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-600 mb-2">{data.label || section}</div>
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-2xl font-bold text-purple-600">{data.avg_scroll_depth}%</div>
                        <div className="text-xs text-gray-500">avg scroll</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-700">{data.max_scroll_depth}%</div>
                        <div className="text-xs text-gray-500">max</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No scroll data available yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Real-time Visitors Tab */}
      {activeTab === 'realtime' && (
        <div className="space-y-6">
          {/* Real-time Summary */}
          <div className="grid md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-lg shadow-sm text-white"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/80 text-sm font-medium">Active Now</div>
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              </div>
              <div className="text-4xl font-bold">
                {realtimeVisitors?.total_active || 0}
              </div>
              <p className="text-xs text-white/70 mt-2">
                Updated: {realtimeVisitors?.timestamp_ist || 'Loading...'}
              </p>
            </motion.div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-gray-600 text-sm font-medium mb-2">By Country</div>
              {realtimeVisitors?.by_country && Object.keys(realtimeVisitors.by_country).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(realtimeVisitors.by_country).slice(0, 3).map(([country, count]) => (
                    <div key={country} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{country}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No active visitors</p>
              )}
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-gray-600 text-sm font-medium mb-2">Current Pages</div>
              {realtimeVisitors?.by_page && Object.keys(realtimeVisitors.by_page).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(realtimeVisitors.by_page).slice(0, 3).map(([page, count]) => (
                    <div key={page} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate max-w-[150px]">{page}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No pages viewed</p>
              )}
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="text-gray-600 text-sm font-medium mb-2">By Device</div>
              {realtimeVisitors?.by_device && Object.keys(realtimeVisitors.by_device).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(realtimeVisitors.by_device).map(([device, count]) => (
                    <div key={device} className="flex items-center justify-between text-sm">
                      <span className="flex items-center text-gray-700">
                        {device === 'Desktop' && <FiMonitor className="mr-1" />}
                        {device === 'Mobile' && <FiSmartphone className="mr-1" />}
                        {device === 'Tablet' && <FiTablet className="mr-1" />}
                        {device}
                      </span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No device data</p>
              )}
            </div>
          </div>

          {/* Active Visitors List */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold text-dark-900 mb-4 flex items-center">
              <FiUsers className="mr-2 text-green-600" />
              Active Visitors
              <span className="ml-3 text-sm font-normal text-gray-500">
                (updates every 10 seconds)
              </span>
            </h2>
            
            {realtimeVisitors?.visitors && realtimeVisitors.visitors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Device</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Browser</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Current Page</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Started</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {realtimeVisitors.visitors.map((visitor, index) => (
                      <motion.tr 
                        key={visitor.session_key}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <FiMapPin className="text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-dark-900">{visitor.country}</div>
                              <div className="text-xs text-gray-500">{visitor.city}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <span className="flex items-center">
                            {visitor.device === 'Desktop' && <FiMonitor className="mr-1 text-gray-400" />}
                            {visitor.device === 'Mobile' && <FiSmartphone className="mr-1 text-gray-400" />}
                            {visitor.device === 'Tablet' && <FiTablet className="mr-1 text-gray-400" />}
                            {visitor.device}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visitor.browser}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate">{visitor.current_page}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            {visitor.session_duration_formatted}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{visitor.session_start_ist}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FiUsers className="mx-auto text-4xl mb-3 text-gray-300" />
                <p>No active visitors at the moment</p>
                <p className="text-sm mt-1">Visitors will appear here when they browse your site</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
